import React from "react";
import { inrCompact } from "../lib/format";
import { accountMode, MODE_CLASS } from "../lib/modes";

/**
 * Top-bar account summary. DISPLAY ONLY — nothing here feeds a trading decision.
 *
 * Two groups, never mixed:
 *   ZERODHA  the logged-in user's OWN broker funds (/api/account/summary). Real money, including
 *            whatever manual positions the account holds. Shown only when the feed is fresh;
 *            otherwise STALE / DISCONNECTED instead of an old number.
 *   GROUNDZERO the user's GroundZero trades in the account's mode (LIVE / SHADOW / PAPER), from the
 *            trade views. Only GroundZero trades are counted — manual positions never are.
 */

export const MODES = { LIVE: "LIVE", SHADOW: "SHADOW", PAPER: "PAPER" };

/** Totals for ONE environment's GroundZero trades (trade views from /paper/v25/trades). */
export function summariseTrades(trades, environment) {
  const t = (trades || []).filter((x) => (x.environment || x.trading_environment || "PAPER") === environment);
  const active = t.filter((x) => x.status !== "COMPLETE");
  const sum = (list, f) => list.reduce((a, x) => a + (Number(f(x)) || 0), 0);
  return {
    total: t.length,
    running: t.filter((x) => x.status === "RUNNING").length,
    complete: t.filter((x) => x.status === "COMPLETE").length,
    openTrades: t.filter((x) => (x.open_levels || 0) > 0).length,
    openPairs: sum(t, (x) => (x.pair_execution_enabled ? x.open_hedged_pairs : 0)),
    used: sum(t, (x) => x.paper_capital_used ?? x.funds_used),
    allocated: sum(t, (x) => x.paper_allocated_capital ?? x.allocated_funds),
    // allocation of trades still in play, and what of it is not yet used
    activeAllocated: sum(active, (x) => x.paper_allocated_capital ?? x.allocated_funds),
    activeRemaining: sum(active, (x) => x.paper_available_funds ?? x.funds_remaining),
    unrealised: sum(t, (x) => x.unrealised_pnl),
    realised: sum(t, (x) => x.realised_pnl),
    // P&L of the trades still in play (cumulative gross, the figure each trade card shows)
    activePnl: sum(active, (x) => x.cumulative_gross_pnl ?? x.combined_pnl),
  };
}

/** Text for one broker-funds value: the number only while the feed is fresh. */
export function fundsValue(funds, key) {
  if (!funds || funds.state === "DISCONNECTED") return "DISCONNECTED";
  if (funds.state !== "LIVE_DATA") return "STALE";
  return funds[key] == null ? "—" : inrCompact(funds[key]);
}

function Stat({ label, value, cls, title, testId }) {
  return (
    <div className="flex flex-col leading-tight whitespace-nowrap" title={title} data-testid={testId}>
      <span className="text-[9px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-[12px] mono font-semibold ${cls || "text-slate-100"}`}>{value}</span>
    </div>
  );
}

const pnlCls = (v) => (v > 0 ? "text-[#00C805]" : v < 0 ? "text-[#FF6b62]" : "text-slate-300");

function Group({ title, cls, testId, children }) {
  return (
    <div className="flex items-center gap-2.5 shrink-0 pl-3 border-l border-[#2A2F3A]" data-testid={testId}>
      <span className={`text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${cls || "text-slate-500"}`}>{title}</span>
      {children}
    </div>
  );
}

export default function AccountSummaryBar({ summary, trades, collapsed }) {
  const mode = summary?.mode || MODES.PAPER;
  const funds = summary?.funds;
  const who = summary?.account?.broker_user_id || funds?.broker_user_id;
  const s = summariseTrades(trades, mode);
  const fundsCls = funds?.state === "LIVE_DATA" ? "text-[#5b9bff]" : funds?.state === "STALE" ? "text-[#FFCC00]" : "text-slate-500";
  const age = funds?.state === "LIVE_DATA" && funds.age_seconds != null ? ` · ${Math.round(funds.age_seconds)}s ago` : "";
  const narrow = collapsed ? "contents" : "hidden 2xl:contents";
  const optionsOnly = summary?.products === "OPTIONS_ONLY";
  const risk = summary?.risk || {};
  const chip = "shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-bold border whitespace-nowrap";
  const acct = accountMode({ products: summary?.products,
    mode: summary?.futures_mode !== undefined ? summary.futures_mode : (summary?.products === "OPTIONS_ONLY" ? null : mode),
    options_mode: summary?.options_mode !== undefined ? summary.options_mode : (summary?.products === "OPTIONS_ONLY" ? mode : null) });
  return (
    <div className="hidden md:flex items-center gap-2.5 min-w-0 overflow-x-auto gz-no-scrollbar" data-testid="account-summary">
      {/* ONE account chip: LIVE when any product of the account trades real money, else SIMULATED (lib/modes);
          each product's exact mode is in the second line and the tooltip */}
      <span data-testid="account-mode" title={acct.title || "This account's trading mode"}
        className={`${chip} ${acct.text === "NOT ENABLED" ? "bg-[#2A2F3A] text-slate-400 border-[#2A2F3A]" : MODE_CLASS[acct.text]}`}>
        {acct.text}{who ? ` · ${who}` : ""}
      </span>
      {acct.detail && <span data-testid="account-mode-detail" className="text-[10px] text-slate-500 whitespace-nowrap">{acct.detail}</span>}
      {(risk.kill_switch || risk.global_kill_switch) && (
        <span data-testid="risk-kill-switch" title={risk.halted_reason || "Kill switch: no new orders; exits and monitoring continue"}
          className={`${chip} bg-[#FF3B30]/20 text-[#FF6b62] border-[#FF3B30]/40`}>KILL SWITCH ON</span>
      )}
      {!risk.kill_switch && risk.stop_new_entries && (
        <span data-testid="risk-stop-entries" title="No new entries; open trades keep being managed and can exit"
          className={`${chip} bg-[#FF9500]/15 text-[#FF9500] border-[#FF9500]/40`}>NEW ENTRIES STOPPED</span>
      )}
      {risk.recon_state && risk.recon_state !== "RECONCILED" && risk.recon_state !== "OK" && (
        <span data-testid="risk-recon" title="Your Zerodha positions and GroundZero's do not agree yet: new entries are blocked until they do"
          className={`${chip} bg-[#FF3B30]/15 text-[#FF6b62] border-[#FF3B30]/40`}>RECONCILIATION {risk.recon_state}</span>
      )}

      <Group title="Zerodha" cls="text-[#5b9bff]" testId="broker-funds-group">
        <Stat label="Available margin" testId="bf-available" cls={fundsCls} value={fundsValue(funds, "available_margin")}
          title={`Zerodha's own available margin for ${who || "your account"} (all segments, single ledger)${age}. Real broker money — includes the effect of any manual positions.`} />
        <Stat label="Used margin" testId="bf-used" cls={fundsCls} value={fundsValue(funds, "used_margin")}
          title="Zerodha utilised margin (debits), all segments — includes manual positions" />
        <div className={narrow}>
          <Stat label="Available cash" testId="bf-cash" cls={fundsCls} value={fundsValue(funds, "available_cash")}
            title="Zerodha available.cash, all segments" />
        </div>
      </Group>

      {optionsOnly ? null : mode === MODES.PAPER ? (
        <Group title="GroundZero SIMULATED · paper" cls="text-[#00C805]" testId="gz-group-paper">
          <div className={narrow}><Stat label="Paper trades" value={s.total} testId="sum-trades" /></div>
          <Stat label="Running" value={s.running} cls={s.running ? "text-[#00C805]" : "text-slate-400"} testId="sum-running" />
          <Stat label="Open trades / pairs" testId="sum-open" value={`${s.openTrades} / ${s.openPairs}`} />
          <Stat label="Capital used / total" testId="sum-capital" value={`${inrCompact(s.used)} / ${inrCompact(s.allocated)}`}
            title="Simulated capital: margin committed across paper trades / total allocated (each paper trade has its own capital)" />
          <Stat label="Unrealised P&L" value={inrCompact(s.unrealised)} testId="sum-unrealised" cls={pnlCls(s.unrealised)} />
          <div className={narrow}><Stat label="Realised P&L" value={inrCompact(s.realised)} testId="sum-realised" cls={pnlCls(s.realised)} /></div>
        </Group>
      ) : (
        <Group title={mode === MODES.LIVE ? "GroundZero LIVE" : "GroundZero SIMULATED · shadow (simulated fills)"}
          cls={mode === MODES.LIVE ? "text-[#FF6b62]" : "text-[#c4b5fd]"} testId={`gz-group-${mode.toLowerCase()}`}>
          <Stat label="Running" value={s.running} testId="gz-running"
            cls={s.running ? "text-[#00C805]" : "text-slate-400"} />
          <Stat label="Alloc · remaining" testId="gz-allocated"
            value={`${inrCompact(s.activeAllocated)} · ${inrCompact(s.activeRemaining)}`}
            title="GroundZero capital allocated to your trades still in play, and how much of it is not yet used — not Zerodha funds" />
          <Stat label={mode === MODES.LIVE ? "LIVE P&L" : "Simulated P&L"} value={inrCompact(s.activePnl)} testId="gz-pnl" cls={pnlCls(s.activePnl)}
            title={mode === MODES.SHADOW
              ? "SHADOW P&L is SIMULATED (Zerodha rejected the orders by design) — not real money"
              : "P&L of your GroundZero LIVE trades still in play (gross). Manual Zerodha positions are not included."} />
        </Group>
      )}
    </div>
  );
}
